import { Component, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ApiService } from '../../services/api.service';
import { HelperService } from '../../services/helper.service';
import { AnimationCollapse } from '../../animations';
import { CookieService } from 'ngx-cookie-service';
import { Subject, firstValueFrom } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { MomentPipe } from '../../pipes/pipes';
import { NgFor, NgClass, NgIf } from '@angular/common';
import { NgxBootstrapMultiselectModule } from 'ngx-bootstrap-multiselect';
import { FormsModule } from '@angular/forms';
import { DeviceService } from 'app/services/device.service';
import { IdleTimeoutService } from 'app/services/idle-timeout.service';
import { EmaNotification, PortalNotificationAdd, PortalNotificationEdit } from 'app/models/notification.model';
import { EmaConfirmDialogComponent } from './ema-confirm-dialog/ema-confirm-dialog.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MultiselectDropdownComponent } from '../multiselect-dropdown/multiselect-dropdown.component';

@Component({
    selector: 'dashboard-admin',
    templateUrl: './dashboard-admin.component.html',
    styleUrls: ['./dashboard-admin.component.css'],
    animations: [
        AnimationCollapse
    ],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [FormsModule, NgxBootstrapMultiselectModule, NgFor, NgClass, NgIf, TranslateModule, MomentPipe, MatDialogModule]
})
export class DashboardAdminComponent implements OnInit, OnDestroy {
  constructor(
    private api: ApiService,
    private translate: TranslateService,
    private cookies: CookieService,
    private router: Router,
    private helper: HelperService,
    private cd: ChangeDetectorRef,
    private deviceService: DeviceService,
    private idleTimeoutService: IdleTimeoutService,
    private dialog: MatDialog 
  ) { };
  //Stores all notifications
  notifications: Notification[];
  //Stores device online/offline information
  onlineStatus: any;
  //Stores network failure data
  networkFailures: any;
  //Model used in notification form
  addNotification: Notification & { durationSecs?: number } = {
    ...new Notification(),
    durationSecs: 12 // default 12 hours
  };
  //Used to store the original notification in case user undo the editing
  originalNotification: Notification;
  //Used to show the undo button in the form
  insertNotificationFormEdit: boolean = false;
  //Used to store the notification index in case of undo
  notificationIndex: number = null;
  //Language selector options
  languages: any;
  //Selected language
  activeLanguage: string;
  //Stores all active old alerts in the system
  oldAlerts: any[];
  oldAlertState: boolean = false;
  oldAlertsLoaded: boolean;

  changeClientError: string = "";

  serverOptions: any[] = [];
  filteredNotifications: Notification[] = [];
  selectedServersForFilter: string[] = [];

  serverSelect = {
    settings: {
      enableSearch: false,
      checkedStyle: 'fontawesome',
      buttonClasses: "btn btn-outline-dark fullWidth dropdown-box",
      dynamicTitleMaxItems: 0,
      selectionLimit: 0,
      autoUnselect: false,
      closeOnSelect: false,
      showCheckAll: true,
      showUncheckAll: true,
      maxHeight: 250,
      enableScroll: true
    },
    texts: {
      defaultTitle: "Select server",
      checkAll: "Select all",
      uncheckAll: "Unselect all"
    }
  }
  customerSelect = {
    settings: {
      enableSearch: true,
      dynamicTitleMaxItems: 1,
      checkedStyle: 'fontawesome',
      containerClasses: "fullWidth",
      buttonClasses: "btn btn-outline-dark fullWidth",
      selectionLimit: 1,
      autoUnselect: true,
      closeOnSelect: true
    },
    texts: { defaultTitle: "" }
  }

  customerList: ICustomerOption[] = null;
  selectedCustomer: ICustomerOption[]

  searchModel: any = {
    keyword: "",
    results: null,
    type: "2",
    searchComplete: true,
    emptyResult: false
  };

  formSubmitSubject = new Subject<void>();
  submitNotification() {
    //Get the language from local storage
    const lang = this.activeLanguage;
  
    // If EMA checkbox is selected, show confirmation dialog
    if (this.addNotification.sendToEma) {
      const dialogRef = this.dialog.open(EmaConfirmDialogComponent, {
        data: { servers: this.addNotification.servers },
        width: '600px'
      });
  
      dialogRef.afterClosed().subscribe(result => {
        if (!result) {
          this.addNotification.sendToEma = false;
          return;
        } else {
          this.proceedSubmit(lang);
        }
      });
      return;
    }
  
    // If EMA is not selected, proceed normally
    this.proceedSubmit(lang);
  }

  proceedSubmit(lang: string) {
    //Change the new notification language to the current language
    this.addNotification.language = lang;
    // Convert boolean to int
    this.addNotification.highlight = this.addNotification.highlight | 0;
    // Portal notifications
   
    if (this.addNotification.sendToPortal) {
      // Edit notification
      const edit = this.insertNotificationFormEdit;
      if (edit) {
        const editData: PortalNotificationEdit = {
          batchId: this.addNotification.batchId,
          language: lang,
          subject: this.addNotification.subject,
          message: this.addNotification.message,
          highlight: this.addNotification.highlight,
          servers: this.addNotification.servers.map(s => ({ serverName: s }))
        };
      
        this.api.submitNotification([editData], true).subscribe({
          next: () => {
            this.loadAllNotifications(lang);
          },
          error: err => console.error('Portal edit failed', err)
        });
      
      } else {
        // add notification
        const addData: PortalNotificationAdd = {
          language: lang,
          subject: this.addNotification.subject,
          message: this.addNotification.message,
          highlight: this.addNotification.highlight | 0,
          servers: [...this.addNotification.servers]
        };
  
        this.api.submitNotification([addData], false).subscribe({
          next: () => this.loadAllNotifications(lang),
          error: err => console.error('Portal add failed', err)
        });
      }
    }

    // EMA notifications
    if (this.addNotification.sendToEma) {
      if (!this.addNotification.servers || this.addNotification.servers.length === 0) {
        console.error("No servers selected for EMA notification");
        return;
      }
  
      const messages: { [language: string]: { Title: string; Body: string } } = {};
      messages[this.addNotification.language] = {
        Title: this.addNotification.subject,
        Body: this.addNotification.message
      };
  
      const durationInSecs = (this.addNotification.durationSecs ?? 12) * 3600;

      const emaData: EmaNotification = {
        serverName: this.addNotification.servers,
        messages: messages,
        durationSecs: durationInSecs,
        pushWindowSecs: 900
      };
  
      this.api.submitEmaNotification(emaData).subscribe({
        next: () => {
          this.loadAllNotifications(this.activeLanguage);
        },
        error: (err) => {
          console.error("EMA notification failed", err);
        }
      });
    }
  
    // Clear form data
    this.addNotification = new Notification();
    this.insertNotificationFormEdit = false;
    this.originalNotification = null;
  }

  //load Portal and EMA notifications
  async loadAllNotifications(lang: string) {
    try {
      const [portalRes, emaRes] = await Promise.all([
        firstValueFrom(this.api.getAllNotificationsForAdmin(lang, 10)),
        firstValueFrom(this.api.getEmaNotifications())
      ]);
  
      const portalNotifs: Notification[] = (portalRes || []).map(n => {
        const serverNames = Array.isArray(n.servers) ? n.servers.map(s => s?.serverName ?? s) : [];
        const allServerNames = this.serverOptions.map(opt => opt.name);
        const visibilityText =
          serverNames.length === 0 || allServerNames.every(name => serverNames.includes(name))
            ? this.translate.instant('ALL_SERVERS')
            : serverNames.join(', ');
  
        return {
          ...n,
          servers: serverNames,
          portalVisibility: visibilityText,
          timeStamp: n.timestamp,
          sendToPortal: true,
          sendToEma: false
        } as Notification;
      });
  
      const emaNotifs: Notification[] = (emaRes?.notifications || []).map(ema => {
        const msg = ema.messages?.[lang];
        if (!msg) return null;
  
        return {
          batchId: ema.notificationId,
          subject: msg.title,
          message: msg.body,
          language: lang,
          sendToEma: true,
          sendToPortal: false,
          isEma: true,
          timeStamp: ema.createdAt,
          emaVisibility: `EMA - ${ema.serverNames?.join(', ') || ''}`
        } as Notification;
      }).filter(n => n !== null);
  
      this.notifications = [...portalNotifs, ...emaNotifs].sort(
        (a, b) => new Date(b.timeStamp).getTime() - new Date(a.timeStamp).getTime()
      );
  
    } catch (err) {
      console.error("Failed to load notifications", err);
    }
  }

  editNotification(notification: Notification) {
    this.notificationIndex = this.notifications.findIndex(
      n => n.batchId === notification.batchId
    );
  
    this.originalNotification = { ...notification };

    this.notifications.splice(this.notificationIndex, 1);

    this.addNotification = {
      ...notification,
      servers: [...notification.servers],
      sendToPortal: true
    };
    //Display undo button
    this.insertNotificationFormEdit = true;
    this.cd.detectChanges();
  }
    
  deleteNotification(notification: Notification) {
    this.notifications = this.notifications.filter(
      n => n.batchId !== notification.batchId
    );
      this.filteredNotifications = this.filteredNotifications.filter(
        n => n.batchId !== notification.batchId
      );
      this.api.deleteNotification(notification.batchId).subscribe({
        error: err => {
          console.error('Delete failed', err);
        }
      });
  }

  undoFormEdit() {
    //Hide undo button
    this.insertNotificationFormEdit = false;
    if (this.notificationIndex !== -1) {
      //Insert the edited notification back to the array to the correct position
      this.notifications.splice(this.notificationIndex, 0, this.originalNotification);
    }
    //Clear form data
    this.addNotification = new Notification();
    this.originalNotification = null;
  }

  //Function which is called whenever user changes to language
  setActiveLanguage(language: any): void {
    //Set selected language as the active language
    this.activeLanguage = language.name;
  
    this.notifications = [];
    this.filteredNotifications = [];
  
    ///Load latest 5 notifications from the server
    this.loadAllNotifications(this.activeLanguage);
  }

  changeClient(): void {
    const selectedCustomer = this.selectedCustomer[0];

    if (!selectedCustomer) {
      this.changeClientError = "ERROR";
      console.error("Error selecting a customer");
      return;
    }

    if (selectedCustomer?.isCompanyGroup) {
      this.changeCustomerGroup(selectedCustomer);
    }
    else {
      this.changeCustomer(selectedCustomer.id, selectedCustomer.name, selectedCustomer.server, null);
    }
  }

  changeCustomerGroup(option: ICustomerOption): void {
    this.changeClientError = "";

    const request = {
      companyGroupId: option.id,
      server: option.server
    }
    this.api.ssoAdminLogin(request).subscribe(res => {
      if (res) {
        // Set old admin data to another cookie and local storage for later use
        this.cookies.set("session-token-admin", this.cookies.get("session-token"), undefined, "/", undefined, false, "Lax");
        localStorage.setItem("user-admin", localStorage.getItem("user"));

        // Clear previous client data
        this.clearClientData();

        const landingPateData = {
          customers: res.customers,
          idProvider: res.user.samaccountname,
          user: res.user
        };

        this.cookies.set("session-token", "Bearer " + res.token, undefined, "/", undefined, false, "Lax");
        localStorage.setItem("landing-page", JSON.stringify(landingPateData));

        this.router.navigate(["landing-page"], {
          queryParams: null
        });
      }
    }, err => {
      console.error(err);
      this.changeClientError = err?.error?.status === 404
        ? "ADMIN_SSO_LOGIN_NOT_SUPPORTED_ERROR"
        : "ERROR";
    });
  }

  changeCustomer(id?: string, name?: string, server?: string, devId?: string): void {
    this.changeClientError = "";

    const data = {
      customerId: id || this.selectedCustomer[0]?.id,
      server: server || this.selectedCustomer[0]?.server,
      name: name || this.selectedCustomer[0]?.name
    }
    this.api.loginAsCustomer(data)
      .subscribe(res => {
        // Set old admin data to another cookie and local storage for later use
        this.cookies.set("session-token-admin", this.cookies.get("session-token"), undefined, "/", undefined, false, "Lax");
        localStorage.setItem("user-admin", localStorage.getItem("user"));

        // Set the token received from the server to the cookie.
        this.cookies.set("session-token", "Bearer " + res.token, undefined, "/", undefined, false, "Lax");

        // Add role numeric values
        switch (res.role) {
          case "User":
            res.roleLevel = 0
            break;
          case "Editor":
            res.roleLevel = 50
            break;
          case "MainUser":
            res.roleLevel = 100
            break;
          case "SystemAdmin":
            res.roleLevel = 200
            break;
          default:
            res.roleLeel = 0
            break;
        }

        // Clear previous client
        this.clearClientData();

        // Set logged in user data into local storage for later use.
        //Exclude token to avoid storing it in a XSS-accessible location (auth is handled via the session-token cookie).
        const { token: _token, ...userWithoutToken } = res;
        localStorage.setItem("user", JSON.stringify(userWithoutToken));
        localStorage.setItem("loggedCustomer", data.name + " (" + data.customerId + ") - " + data.server);

        // Start idle timeout monitoring after successful login and if automatic logout minutes are set
        if (res.automaticLogoutMinutes && res.automaticLogoutMinutes > 0) {
          this.idleTimeoutService.startWatching(res.automaticLogoutMinutes);
        }

        if (devId) {
          this.router.navigate(['device/' + devId]);
        } else {
          this.router.navigate(['dashboard']);
        }
      }, err => {
        this.changeClientError = "ERROR";
        console.error(err);
      })
  }

  clearClientData(): void {
    localStorage.removeItem("features");
    localStorage.removeItem("customerInfo");
    localStorage.removeItem("user");
    localStorage.removeItem("loggedCustomer");
    localStorage.removeItem("landing-page");
  }

  getActivationName(group: number, node: number, type: string, customAct: string, connTimeLimit: number): string {
    return this.helper.getActivationName(group, node, type, customAct, connTimeLimit);
  }

  search(): void {
    this.searchModel.emptyResult = false;
    this.searchModel.results = null;
    this.searchModel.searchComplete = false;
    this.api.search(this.searchModel.type, encodeURIComponent(this.searchModel.keyword))
      .subscribe(res => {
        this.searchModel.searchComplete = true;
        //Check if result is empty
        this.checkResult(res);
        this.searchModel.results = res;
      }, err => {
        //In case of error set search as complete, empty array as result and empty result to true. Display empty search result error in UI
        this.searchModel.searchComplete = true;
        this.searchModel.results = [];
        this.searchModel.emptyResult = true;
      })
  }

  checkResult(data: any): void {
    //If all arrays in result are empty then display error for empty result
    if ((data.devices && data.devices.length === 0) &&
      (data.locations && data.locations.length === 0) &&
      (data.customers && data.customers.length === 0) &&
      (data.personnel && data.personnel.length === 0) &&
      (data.alarmRoutes && data.alarmRoutes.length === 0) &&
      (data.keys && data.keys.length === 0)) {
      this.searchModel.emptyResult = true;
    }
  }

  loadActiveAlerts(): void {
    this.oldAlertsLoaded = false;
    this.api.getActiveAlerts(true)
      .subscribe(res => {
        this.oldAlertsLoaded = true;
        if (res) {
          this.oldAlerts = res;
        } else {
          this.oldAlerts = null;
        }
      })
  }

  cancelAlert(alert: any): void {
    this.api.cancelActiveAlert(alert.taskId, alert.server)
      .subscribe(res => {
        alert.cancelled = true;
      },
        err => {
          alert.error = err;
        })
  }

  openOldAlerts(): void {
    this.oldAlertState = !this.oldAlertState;
    if (!this.oldAlertState) {
      this.oldAlerts = null;
      return;
    }
    //Check that activation translations are loaded into local storage before loading active alerts. Don't load translations again if they are in local storage
    if (!localStorage.getItem("act")) {
      this.api.getActivationTranslations(this.activeLanguage)
        .subscribe((response: any) => {
          localStorage.setItem("act", JSON.stringify(response));
          //Load active alerts when activation alerts are loaded
          this.loadActiveAlerts();
        });
    } else {
      //Load active alerts if translations were already in local storage
      this.loadActiveAlerts();
    }
  }

  checkAdminStatus(): void {
    const user: any = JSON.parse(localStorage.getItem("user"));
    if (user && user.roleLevel <= 100) {
      window.location.href = 'dashboard';
    }
    if (this.cookies.get("session-token-admin")) {
      this.cookies.set("session-token", this.cookies.get("session-token-admin"), undefined, "/", undefined, false, "Lax")
      this.cookies.delete("session-token-admin", "/", undefined, false, "Lax");
      const user = localStorage.getItem("user-admin");
      const lang = localStorage.getItem("language");
      localStorage.clear();
      localStorage.setItem("user", user);
      localStorage.setItem("language", lang);
      window.location.href = 'dashboard/admin';
    }
  }

  getDevType(type: string): string {
    return this.api.getDeviceType(type, false);
  }

  get highlightDisabled(): boolean {
    const selected = this.addNotification.servers ?? [];
    const all = this.serverOptions ?? [];
  
    const allServersSelected =
      all.length > 0 && selected.length === all.length;
  
    const portalSelected = this.addNotification.sendToPortal;
    const disabled = !(portalSelected && allServersSelected);
  
    if (disabled && this.addNotification.highlight) {
      this.addNotification.highlight = 0;
    }
    return disabled;
  }
  
  onServerSelectionChange(selected: string[]) {
    this.addNotification.servers = selected;
    if (this.highlightDisabled) {
      this.addNotification.highlight = 0;
    }
  }

  ngOnInit() {
    this.api.getAvailableServers().subscribe(
      servers => {
        this.serverOptions = servers.map(s => ({
          id: s,
          name: s
        }));
      },
      error => console.error("Error loading servers:", error)
    );
    this.checkAdminStatus();
    this.languages = [
      {
        imgUrl: 'assets/img/flags/fi.png',
        name: 'fi'
      },
      {
        imgUrl: 'assets/img/flags/sv.png',
        name: 'sv'
      },
      {
        imgUrl: 'assets/img/flags/uk.png',
        name: 'en'
      },
      {
        imgUrl: 'assets/img/flags/ee.png',
        name: 'ee'
      }
    ];
    //Get language from the local storage
    const lang = localStorage.getItem("language");
    this.activeLanguage = lang;
    //Load latest notifications from the server
    if (this.activeLanguage) {
      this.loadAllNotifications(this.activeLanguage);
    }    
    //Load online status from all of the servers
    this.api.getOnlineStatus(true)
      .subscribe((response: any) => {
        this.onlineStatus = response;
      });
    //Load all available customers
    this.api.getAllCustomers()
      .subscribe((res: ICustomerOption[]) => {
        if (res) {
          const list = res.map(option => {
            const a: any = {};
            a.id = option;
            a.name = `${option.name} (${option.id}) - ${option.postOffice} - ${option.isCompanyGroup ? "Landing page - " : ""}${option.server}`;
            return a;
          })
          this.customerList = list;
          this.cd.detectChanges();
        }
      })

    //Load network failure data
    this.api.getNetworkFailures()
      .subscribe(res => {
        this.networkFailures = res;
      })

    this.translate.get(['CUSTOMER']).subscribe(t => {
      this.customerSelect.texts.defaultTitle = t.CUSTOMER
    })

    // Initialize form submit subject with 3 second throttle time to prevent multiple submits
    this.formSubmitSubject
      .pipe(throttleTime(3000))
      .subscribe(() => {
        this.submitNotification();
      });
  }

  ngOnDestroy(): void {
    this.formSubmitSubject.unsubscribe();
  }
}

interface ICustomerOption {
  id: string;
  name: string;
  postOffice: string;
  server: string;
  isCompanyGroup: boolean;
}

class Notification {
  batchId: string;
  language: string;
  subject: string;
  message: string;
  highlight: number = 0;
  sendToEma: boolean = false;
  sendToPortal: boolean = false;
  servers: string[] = [];
  serverResults?: { serverName: string; serverId: number }[];
  timeStamp: string; 
  portalVisibility?: string;
  emaVisibility?: string;
  isEma?: boolean;   
}
